import { TmapApp } from '@tmap-web-lib/tmap-app-interface'
import { useCallback, useMemo, useState } from 'react'
import { coerce, gte } from 'semver'

import { useGetServiceConfiguration } from '@/react-query'

// 서비스 설정 응답이 없거나 실패했을 때의 폴백 최소 버전.
// UI 게이트 계산에도 이 값이 초기값으로 사용된다.
const DEFAULT_MIN_VERSION = { AOS: '11.5.0', iOS: '11.5.0' } as const

type VersionConfig = { AOS: string; iOS: string }

function isAppVersionSupported(config: VersionConfig): boolean {
  if (!TmapApp.env.isInApp) return true // TODO: 로컬 확인용 임시 — 확인 후 제거
  const osKey = TmapApp.env.isAndroid ? 'AOS' : 'iOS'
  const minVersion = coerce(config[osKey])?.version
  const appVersion = coerce(TmapApp.env.appVersion)?.version

  // coerce에 실패하면 판단 불가 → 허용 쪽으로 (기존 checkVersion 정책과 일치)
  if (!minVersion || !appVersion) return true

  return gte(appVersion, minVersion)
}

export function useShortFormVersionCheck() {
  const [showUpdateDialog, setShowUpdateDialog] = useState(false)
  const [requiredVersion, setRequiredVersion] = useState('')

  const { data: shortFormVersionData } = useGetServiceConfiguration<VersionConfig>(
    { key: 'MY_SHORT_FORM_RELEASE_VERSION' },
    { useErrorBoundary: false },
  )

  const versionConfig: VersionConfig = shortFormVersionData?.data?.contents?.[0]?.data ?? DEFAULT_MIN_VERSION

  // 렌더 게이트용 pre-check: 데이터 도착 전엔 디폴트 기준으로 판단되므로 즉시 사용 가능.
  const isShortFormAvailable = useMemo(() => isAppVersionSupported(versionConfig), [versionConfig])

  const checkVersion = useCallback((): boolean => {
    if (isAppVersionSupported(versionConfig)) return true

    const osKey = TmapApp.env.isAndroid ? 'AOS' : 'iOS'
    setRequiredVersion(versionConfig[osKey])
    setShowUpdateDialog(true)
    return false
  }, [versionConfig])

  const closeUpdateDialog = useCallback(() => {
    setShowUpdateDialog(false)
  }, [])

  return { showUpdateDialog, requiredVersion, checkVersion, closeUpdateDialog, isShortFormAvailable }
}
